/**
 * Single source of truth for plan limit numbers shown on /pricing and Manage Account feature limits.
 * Update values here — both pages read from this config.
 */
export type StandardPlanKey = 'FREE' | 'STANDARD' | 'PROFESSIONAL';
export type PlanKey = StandardPlanKey | 'ENTERPRISE' | 'CUSTOM';

export interface PlanLimitNumbers {
  projects: number;
  collectionsPerProject: number;
  requestsPerProject: number;
  dataSetsWorkspace: number;
  assertionsWorkspace: number;
  variablesWorkspace: number;
  environments: number;
  testPlans: number;
  apiExecutionsPerMonth: number;
  users?: number;
}

export interface PlanLimitLabelStrings {
  projects?: string;
  collections?: string;
  requests?: string;
  dataSets?: string;
  variables?: string;
  environments?: string;
  assertions?: string;
  testSuites?: string;
}

export interface PlanPricingMeta {
  name: string;
  price?: string;
  monthlyPrice?: string;
  yearlyPrice?: string;
  yearlyMonthlyAmount?: number;
  yearlyTotal?: number;
  period?: string;
  periodYearly?: string;
  supportNote: string;
  perSeatLicense?: boolean;
}

export interface AddonPricingMeta {
  name: string;
  price: string;
  period: string;
  description: string;
}

/** Plan capacity numbers (aligned with backend PlanLimitsService.normalizeStandardPlanCaps). */
export const PLAN_LIMIT_NUMBERS: Record<StandardPlanKey, PlanLimitNumbers> = {
  FREE: {
    projects: 1,
    collectionsPerProject: 10,
    requestsPerProject: 50,
    dataSetsWorkspace: 500,
    assertionsWorkspace: 2_000,
    variablesWorkspace: 5_000,
    environments: 3,
    testPlans: 3,
    apiExecutionsPerMonth: 500,
    users: 1,
  },
  STANDARD: {
    projects: 3,
    collectionsPerProject: 30,
    requestsPerProject: 200,
    dataSetsWorkspace: 5_000,
    assertionsWorkspace: 25_000,
    variablesWorkspace: 25_000,
    environments: 3,
    testPlans: 3,
    apiExecutionsPerMonth: 5_000,
  },
  PROFESSIONAL: {
    projects: 10,
    collectionsPerProject: 100,
    requestsPerProject: 1_000,
    dataSetsWorkspace: 50_000,
    assertionsWorkspace: 250_000,
    variablesWorkspace: 250_000,
    environments: 5,
    testPlans: 5,
    apiExecutionsPerMonth: 50_000,
  },
};

export const PLAN_PRICING_META: Record<StandardPlanKey | 'ENTERPRISE', PlanPricingMeta> = {
  FREE: {
    name: 'Free',
    price: '$0',
    period: 'per month / 1 user',
    supportNote: 'Community support',
  },
  STANDARD: {
    name: 'Standard',
    monthlyPrice: '$39',
    yearlyPrice: '$33.25',
    yearlyMonthlyAmount: 33.25,
    yearlyTotal: 399,
    price: '$39',
    period: 'per month/per user',
    periodYearly: 'per month/per user (billed yearly)',
    supportNote: 'Community support',
    perSeatLicense: true,
  },
  PROFESSIONAL: {
    name: 'Professional',
    monthlyPrice: '$79',
    yearlyPrice: '$66.58',
    yearlyMonthlyAmount: 66.58,
    yearlyTotal: 799,
    price: '$79',
    period: 'per month/per user',
    periodYearly: 'per month/per user (billed yearly)',
    supportNote: 'Dedicated support (1 business day SLA)',
    perSeatLicense: true,
  },
  ENTERPRISE: {
    name: 'Enterprise',
    price: 'Custom',
    period: 'pricing',
    supportNote: 'Dedicated support',
  },
};

export const ADDON_PRICING_CATALOG: AddonPricingMeta[] = [
  {
    name: '+25,000 API executions',
    price: '$29',
    period: 'per month',
    description: 'Extra automated test plan runs for heavy CI pipelines.',
  },
  {
    name: '+5 projects',
    price: '$19',
    period: 'per month',
    description: 'Expand workspace capacity without upgrading your plan tier.',
  },
  {
    name: 'Extended run history',
    price: '$15',
    period: 'per month',
    description: 'Keep test run results for 365 days instead of 90 days.',
  },
  {
    name: '+10 environments per project',
    price: '$9',
    period: 'per month',
    description: 'Adds 10 environment slots to every project in your workspace.',
  },
];

const UNLIMITED_LABELS: PlanLimitLabelStrings = {
  projects: 'Unlimited',
  collections: 'Unlimited',
  requests: 'Unlimited',
  dataSets: 'Unlimited',
  variables: 'Unlimited',
  environments: 'Unlimited',
  assertions: 'Unlimited',
  testSuites: 'Unlimited',
};

const fmt = (n: number) => n.toLocaleString('en-US');

export function planLimitLabelsFromConfig(planKey: string): PlanLimitLabelStrings | undefined {
  const key = planKey.trim().toUpperCase();
  if (key === 'ENTERPRISE' || key === 'CUSTOM') {
    return UNLIMITED_LABELS;
  }
  const limits = PLAN_LIMIT_NUMBERS[key as StandardPlanKey];
  if (!limits) {
    return undefined;
  }
  return {
    projects: String(limits.projects),
    collections: String(limits.collectionsPerProject),
    requests: String(limits.requestsPerProject),
    dataSets: String(limits.dataSetsWorkspace),
    variables: String(limits.variablesWorkspace),
    environments: String(limits.environments),
    assertions: String(limits.assertionsWorkspace),
    testSuites: String(limits.testPlans),
  };
}

/** Fallback caps for Manage Account when API omits limit fields. */
export const DEFAULT_LIMIT_LABELS_BY_PLAN: Record<string, PlanLimitLabelStrings> = {
  FREE: planLimitLabelsFromConfig('FREE')!,
  STANDARD: planLimitLabelsFromConfig('STANDARD')!,
  PROFESSIONAL: planLimitLabelsFromConfig('PROFESSIONAL')!,
  ENTERPRISE: UNLIMITED_LABELS,
  CUSTOM: UNLIMITED_LABELS,
};

export function buildStandardPlanFeatureBullets(planKey: StandardPlanKey): string[] {
  const limits = PLAN_LIMIT_NUMBERS[planKey];
  const meta = PLAN_PRICING_META[planKey];
  const bullets: string[] = [
    `${limits.projects} project${limits.projects === 1 ? '' : 's'}`,
    `${limits.collectionsPerProject} collections per project`,
    `${limits.requestsPerProject} requests per project`,
    `${fmt(limits.dataSetsWorkspace)} data sets (workspace)`,
    `${fmt(limits.assertionsWorkspace)} assertions (workspace)`,
    `${fmt(limits.variablesWorkspace)} variables (workspace)`,
    `${limits.environments} environments per project`,
    `${limits.testPlans} test plan${limits.testPlans === 1 ? '' : 's'} per project`,
    `${fmt(limits.apiExecutionsPerMonth)} API executions per month`,
  ];
  if (limits.users != null && planKey !== 'FREE') {
    bullets.push(`${limits.users} user`);
  }
  if (meta.perSeatLicense) {
    bullets.push('Per-seat license');
  }
  bullets.push(meta.supportNote);
  return bullets;
}

export const ENTERPRISE_FEATURE_BULLETS: string[] = [
  'Unlimited projects',
  'Unlimited collections',
  'Unlimited requests',
  'Unlimited data sets per request',
  'Unlimited assertions per data set',
  'Unlimited variables per data set',
  'Unlimited environments',
  'Unlimited test suites',
  'Schema-per-tenant architecture',
  'Complete data isolation',
  'Dedicated support',
  'Custom SLA',
  'On-premise option',
  'Advanced security',
];
